/**
 * Analytics event catalog — consumer / payer surfaces.
 * Keep names stable once shipped; prefer props over renaming.
 */
export const PayerEvents = {
  // Session / entry
  FLOW_INITIATED: "[Payer View] - Flow Initiated",
  PAYMENT_LINK_OPENED: "[Payer View] - Payment Link Opened",
  INVOICE_CONSENT_OPENED: "[Payer View] - Invoice Consent Opened",
  CONSUMER_PORTAL_INITIATED: "[Consumer View] - Initiated",
  CUSTOMER_PORTAL_INITIATED: "[Customer Portal] - Initiated",
  LANGUAGE_SWITCHED: "Language Switched",

  // Steps (page views)
  STEP_INVOICE_DETAILS: "[Payer View] - Invoice Details",
  STEP_CUSTOMER_DETAILS: "[Payer View] - Customer Details",
  STEP_PAYMENT_LINK_INTRO: "[Payer View] - Payment Link Customer Details",
  STEP_PAYMENT_LINK_FORM: "[Payer View] - Payment Link Custom Form",
  STEP_PAYMENT_LINK_PLAN: "[Payer View] - Payment Link Plan",
  STEP_QURRAH: "[Payer View] - Qurrah Payment",
  STEP_EARLY_SETTLEMENT: "[Payer View] - Early Settlement",
  STEP_CARD_PAYMENT: "[Payer View] - Card Payment - Moyasar",
  STEP_COMPLETE_PAYMENT: "[Payer View] - Complete Payment",
  STEP_MANUAL_PAYMENT: "[Payer View] - Manual Payment",
  STEP_BANK_TRANSFER: "[Payer View] - Bank Transfer",

  // Navigation / proceed
  STEP_PROCEEDED: "[Payer View] - Step Proceeded",
  STEP_BACK: "[Payer View] - Step Back",

  // Payment type / plan
  SELECT_PAYMENT_TYPE: "[Payer View] - Select Payment Type",
  PAYMENT_TYPE_SELECTED: "[Payer View] - Payment Type Selected",
  INSTALLMENT_TYPE_SELECTED: "[Payer View] - Installment Type Selected",
  PLAN_SELECTED: "[Payer View] - Payment Plan Selected",
  QUANTITY_CHANGED: "[Payer View] - Payment Link Quantity Changed",

  // Customer / form
  CUSTOMER_DETAILS_SUBMITTED: "[Payer View] - Customer Details Submitted",
  CUSTOMER_DETAILS_VALIDATION_FAILED:
    "[Payer View] - Customer Details Validation Failed",
  CUSTOM_FORM_SUBMITTED: "[Payer View] - Custom Form Submitted",
  CUSTOM_FORM_VALIDATION_FAILED: "[Payer View] - Custom Form Validation Failed",
  REFUND_POLICY_TOGGLED: "[Payer View] - Refund Policy Toggled",

  // Invoice preview / PDF
  PREVIEW_INVOICE_CLICKED: "[Payer View] - Preview Invoice Clicked",
  PREVIEW_INVOICE_OPENED: "[Payer View] - Preview Invoice Opened",
  PREVIEW_INVOICE_CLOSED: "[Payer View] - Preview Invoice Closed",
  DOWNLOAD_INVOICE_PDF: "[Payer View] - Download Invoice PDF",

  // Payment methods / Moyasar
  MOYASAR_FORM_LOADED: "[Payer View] - Moyasar Payment Form Loaded",
  MOYASAR_PAYMENT_INITIATED: "[Payer View] - Moyasar Payment Initiated",
  MOYASAR_PAYMENT_COMPLETED: "[Payer View] - Moyasar Payment Completed",
  MOYASAR_APPLE_PAY_FAILED: "[Payer View] - Moyasar Apple Pay Payment Failed",
  MOYASAR_PAYMENT_ERROR: "[Payer View] - Error in payment processing",
  STEP_CARD_PAYMENT_MYFATOORAH: "[Payer View] - Card Payment - MyFatoorah",
  MYFATOORAH_FORM_LOADED: "[Payer View] - MyFatoorah Payment Form Loaded",
  MYFATOORAH_PAYMENT_COMPLETED: "[Payer View] - MyFatoorah Payment Completed",
  MYFATOORAH_REDIRECT_FOR_AUTHENTICATION:
    "[Payer View] - MyFatoorah Redirect For Authentication",
  MYFATOORAH_PAYMENT_FAILED: "[Payer View] - MyFatoorah Payment Failed",
  PAYMENT_METHOD_SELECTED: "[Payer View] - Payment Method Selected",
  INTENT_CREATED: "[Payer View] - Payment Intent Created",
  INTENT_CREATE_FAILED: "[Payer View] - Payment Intent Create Failed",

  // Qurrah / early settlement actions
  QURRAH_ALREADY_PAID_SUBMITTED: "[Payer View] - Qurrah Already Paid Submitted",
  QURRAH_ALREADY_PAID_FAILED: "[Payer View] - Qurrah Already Paid Failed",
  EARLY_SETTLEMENT_SELECTED: "[Payer View] - Early Settlement Selected",
  EARLY_SETTLEMENT_PROCEEDED: "[Payer View] - Early Settlement Proceeded",

  // Outcomes
  PAYMENT_SUCCESS: "[Payer View] - Payment Success",
  PAYMENT_FAILED: "[Payer View] - Payment Failed",
  SUBSCRIPTION_AUTO_RENEWAL_SUCCESS:
    "[Payer View] - Subscription Auto Renewal Success",
  SUBSCRIPTION_AUTO_RENEWAL_FAILED:
    "[Payer View] - Subscription Auto Renewal Failed",

  // Bank transfer (legacy)
  BANK_TRANSFER_PROCEED: "[Payer View] - Bank Transfer - Proceed to Payment",
  BANK_SELECTED: "[Payer View] - Bank Selected",

  // Consumer portal
  CONSUMER_AUTH_VIEW: "[Consumer View] - Auth",
  CONSUMER_HOME_VIEW: "[Consumer View] - Home",
  CONSUMER_INVOICE_DETAIL: "[Consumer View] - Invoice Detail",
  CONSUMER_SETTINGS_VIEW: "[Consumer View] - Settings",
  CONSUMER_CARDS_VIEW: "[Consumer View] - Cards",
  CONSUMER_ADD_CARD: "[Consumer View] - Add Card",
  CONSUMER_SUBSCRIPTION_CANCEL: "[Consumer View] - Subscription Cancel Clicked",
  CONSUMER_SUBSCRIPTION_PAUSE: "[Consumer View] - Subscription Pause Clicked",
  CONSUMER_SUBSCRIPTION_UNPAUSE:
    "[Consumer View] - Subscription Unpause Clicked",

  // Customer portal
  CUSTOMER_PORTAL_VIEW: "[Customer Portal] - View",
  CUSTOMER_PORTAL_ACTION: "[Customer Portal] - Action",
} as const;

export type PayerEventName = (typeof PayerEvents)[keyof typeof PayerEvents];
